import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import _ from "lodash";

import {
    Button,
    Modal,
    Form,
    Input,
    message,
    Spin,
    Select,
} from "antd";

import { LoadingOutlined } from "@ant-design/icons";
import { useCaseViewContext } from '../../../context';
import { useUSStatesContext } from '../../../context';

import {
    StyledInputNumber,
    StyledInput,
    StyledDatePicker,
    StyledButtonFormItem,
    StyledFlexDiv,
} from './EditPatient.styles';

const EditPatient = ({
    onClose,
}) => {

    const { 
        caseDetails, 
        fetchCaseDetails,
        apiClasses,
        apiType, 
    } = useCaseViewContext();

    const { usStates } = useUSStatesContext();

    if (!caseDetails) {
        return null;
    }

    const { patient } = caseDetails;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleChange = (_, allValues) => {
        if (allValues.dob) {
            let patientAge = dayjs().diff(allValues.dob, "year");
            form.setFieldsValue({ age: patientAge });
        }
    }

    const handleFinish = () => {
        message.success("Patient details updated successfully");
        onClose();
    }

    const updatePatientDetails = useCallback(async () => {

        if (!apiClasses?.CasesApi && !apiClasses?.PhysicianAPI) {
            message.error("API is not available");
            return;
        }

        const values = form.getFieldsValue();
        if (dayjs(patient.dob).utc().format('MM/DD/YYYY') !== values.dob.format('MM/DD/YYYY')) {
            values.overrideHeisenbergDob = true;
        } else {
            delete values.dob;
        }

        try {
            setLoading(true);
            const { data } = apiType === 'physician' 
                ? await apiClasses.PhysicianAPI.updatePatientDetails(caseDetails.caseId, values)
                : await apiClasses.CasesApi.updateCasePatientDetails(caseDetails.caseId, values);
            if (!data.success) throw new Error(data.message);
            return handleFinish();
        } catch (e) {
            const errorMessage = e.response?.data?.message || "Unable to update Patient details, Please try again!";
            console.error(e);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [apiClasses, apiType, caseDetails.caseId, form, patient]);

    useEffect(() => {
        return () => {
            form.resetFields();
            fetchCaseDetails();
        }
    }, [form, fetchCaseDetails]);

    return (
        <Modal
            title={"Edit Patient Data"}
            visible
            onCancel={onClose}
            footer={[]}
        >
            <Spin spinning={loading} indicator={<LoadingOutlined />}>
                <Form
                    form={form}
                    layout={"vertical"}
                    name="patient-edit-modal"
                    onValuesChange={handleChange}
                    onFinish={updatePatientDetails}
                    initialValues={{
                        ...patient,
                        state: patient.state?.name || patient.state,
                        dob: dayjs.utc(patient.dob),
                        age: dayjs().diff(dayjs(patient.dob), "year")
                    }}
                >
                    <StyledFlexDiv>
                        <Form.Item
                            name="firstName"
                            label="First Name"
                            rules={[
                                {
                                    required: true,
                                    message: "'First Name' required",
                                },
                            ]}
                        >
                            <Input id="edit-patient-first-name-input" />
                        </Form.Item>
                        <Form.Item
                            name="lastName"
                            label="Last Name"
                            rules={[
                                {
                                    required: true,
                                    message: "'Last Name' required",
                                },
                            ]}
                        >
                            <Input id="edit-patient-last-name-input" />
                        </Form.Item>
                    </StyledFlexDiv>
                    <Form.Item
                        name="address1"
                        label="First Address"
                        rules={[
                            {
                                required: true,
                                message: "'First Address' required",
                            },
                        ]}
                    >
                        <Input id="edit-patient-address1-input" />
                    </Form.Item>
                    <Form.Item name="address2" label="Second Address">
                        <Input id="edit-patient-address2-input" />
                    </Form.Item>

                    <StyledFlexDiv>
                        <Form.Item
                            name="city"
                            label="City"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Input id="edit-patient-city-input" />
                        </Form.Item>

                        <Form.Item
                            name="state"
                            label="State"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select 
                                id="edit-patient-state-select"
                                showSearch
                                filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                options={usStates.map(state => ({ key: state.id, value: state.name, label: state.name }))} 
                            />
                        </Form.Item>
                    </StyledFlexDiv>

                    <Form.Item
                        name="dob"
                        label="DOB"
                        rules={[
                            {
                                required: true,
                            },
                        ]}
                    >
                        <StyledDatePicker
                            id="edit-patient-dob-datepicker"
                            disabledDate={val => val && val.valueOf() > Date.now()}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            format={"MM/DD/YYYY"}
                        />
                    </Form.Item>
                    <StyledFlexDiv>
                        <Form.Item
                            name="age"
                            label="Age"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <StyledInputNumber id="edit-patient-age-input" disabled={true} />
                        </Form.Item>
                        <Form.Item
                            name="zipCode"
                            label="Zip Code"
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <StyledInput id="edit-patient-zip-code-input" />
                        </Form.Item>
                    </StyledFlexDiv>

                    <Form.Item
                        name="email"
                        label="E-mail"
                        rules={[
                            {
                                type: "email",
                                message: "The input is not valid E-mail!",
                            },
                            {
                                required: true,
                            },
                        ]}
                    >
                        <Input id="edit-patient-email-input" />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="Phone"
                        rules={[
                            {
                                required: true,
                            },
                        ]}
                    >
                        <StyledInputNumber id="edit-patient-phone-input" />
                    </Form.Item>

                    <StyledButtonFormItem>
                        <Button
                            id="edit-patient-cancel-btn"
                            size={"large"}
                            onClick={onClose}
                            disabled={loading}
                        >
                            cancel
                        </Button>
                        <Button
                            id="edit-patient-submit-btn"
                            type="primary"
                            size={"large"}
                            loading={loading}
                            htmlType="submit"
                        >
                            submit
                        </Button>
                    </StyledButtonFormItem>
                </Form>
            </Spin>
        </Modal>
    );
};

export default EditPatient;
